import { CaseState } from '@constants/caseStates';
import { WorkflowType } from '@constants/dispositions';

/**
 * Checks if the given state is a human call state.
 * Handles both generic 'human_call' and phase-specific states like 'primary_human_call', 'secondary_human_call'.
 *
 * @param state - The case state to check
 * @returns true if the state is a human call state
 */
export const isHumanCallState = (state?: string): boolean => {
  if (!state) return false;
  return state.endsWith('_human_call') || state === CaseState.HUMAN_CALL;
};

/**
 * Whether the case's human call is live: a human-call state with an active call.
 * Such a case cannot be reassigned (LA-8061); the server rejects it too.
 *
 * @param state - The case state
 * @param activeCallId - case_record.active_call_id
 */
export const isHumanCallInProgress = (state?: string, activeCallId?: string): boolean =>
  isHumanCallState(state) && !!activeCallId;

/**
 * Checks if the given state is a human audit state.
 * Handles both generic 'human_audit' and phase-specific states like 'primary_human_audit', 'secondary_human_audit'.
 * Also includes eligibility check human audit states.
 *
 * @param state - The case state to check
 * @returns true if the state is a human audit state
 */
export const isHumanAuditState = (state?: string): boolean => {
  if (!state) return false;
  return state.endsWith('_human_audit') || state === CaseState.HUMAN_AUDIT;
};

/**
 * Whether a case is a refill case parked in human_audit_needed (JNJ-172), where a
 * human fixes the data before pressing Retry. Permission still comes from the
 * can_update_* flags the refill workflow sets on entering that state; this only
 * tells the Summary view to show its output editor there, which it otherwise does
 * only in hermes' human_audit.
 */
export const isRefillHumanAuditNeeded = (workflowType?: string, state?: string): boolean =>
  workflowType === WorkflowType.REFILL && state === CaseState.REFILL_HUMAN_AUDIT_NEEDED;

/**
 * Checks if the given state is an automated themis AI-audit state, where a
 * Cancel AI Audit rolls the case back to human_audit (BV-2131). Covers the audit
 * of the AI call (`ai_audit`, `ai_call_audit`), of a human call
 * (`human_call_ai_audit`, BI-1839), and of the eligibility check (`ec_ai_audit`).
 * Mirrors the backend `isAIAuditState`.
 *
 * @param state - The case state to check
 * @returns true if the state is an automated AI-audit state
 */
export const isAIAuditState = (state?: string): boolean => {
  return (
    state === CaseState.AI_AUDIT ||
    state === CaseState.AI_CALL_AUDIT ||
    state === CaseState.HUMAN_CALL_AI_AUDIT ||
    state === CaseState.EC_AI_AUDIT
  );
};

/**
 * Checks if the given state is an eligibility check state (automated).
 * This includes both primary and secondary eligibility check states.
 *
 * @param state - The case state to check
 * @returns true if the state is an automated eligibility check state
 */
export const isEligibilityCheckState = (state?: string): boolean => {
  if (!state) return false;
  return (
    state === CaseState.PRIMARY_ELIGIBILITY_CHECK ||
    state === CaseState.SECONDARY_ELIGIBILITY_CHECK ||
    (state.includes('eligibility_check') && !state.includes('human_audit'))
  );
};

/**
 * Checks if the given state is an eligibility check human audit state.
 * This includes both primary and secondary eligibility check human audit states.
 *
 * @param state - The case state to check
 * @returns true if the state is an eligibility check human audit state
 */
export const isEligibilityCheckHumanAuditState = (state?: string): boolean => {
  if (!state) return false;
  return (
    state === CaseState.PRIMARY_ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    state === CaseState.SECONDARY_ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    state === CaseState.ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    state.endsWith('_eligibility_check_human_audit')
  );
};

/**
 * Checks if notes can be added to a case based on its state.
 * Notes cannot be added when the case is in pre_processing, completed, or terminated states.
 *
 * @param state - The case state to check
 * @returns true if notes can be added to the case
 */
export const canAddNoteToCase = (state?: string): boolean => {
  if (!state) return false;
  const terminalStates: ReadonlySet<string> = new Set([
    CaseState.PRE_PROCESSING,
    CaseState.COMPLETED,
    CaseState.TERMINATED,
  ]);
  return !terminalStates.has(state);
};
